import type { State } from '../../types';
import { orderBy, sortBy } from 'lodash';
import { riskAssessmentsReducedByName } from './risk-assessments-reduced-by-name';
import { toListItem } from './to-list-item';
import type { RiskAssessmentGroup } from '../../../pages/risk-assessments-page/risk-assessment-group';
import { pageIds } from '../pages/pageIds';
import { getPageGroups } from '../pages/getPageGroups';
import type { PageGroupItemViewModel } from '@trinnvis/dabih-api-client';

export function getRiskAssessmentGroups(state: State, hrefPrefix: string): RiskAssessmentGroup[] {
  const groups: RiskAssessmentGroup[] = [];
  const themePageGroup = getPageGroups(state).find((group) => {
    return group.code === 'themePages';
  });
  const registryIds = getPageGroups(state)
    .filter((group) => group.code === 'registers')
    .flatMap((group) => group.pages)
    .map((p) => p.pageId);
  if (themePageGroup) {
    const themePages: PageGroupItemViewModel[] = themePageGroup.pages;
    const categories: { pageId: number; name: string; substance: boolean; dataItem: boolean }[] = [];
    themePages.forEach((page) => {
      categories.push({ pageId: page.pageId, name: page.name, substance: false, dataItem: false });
      if (page.pageId === pageIds.informationSecurity) {
        categories.push({
          pageId: 0,
          name: 'Data\u00ADsikker\u00ADhet: Person\u00ADregister',
          substance: false,
          dataItem: true,
        });
      }
    });
    if (registryIds.includes(pageIds.substances)) {
      categories.push({ pageId: 0, name: 'HMS: Stoffer', substance: true, dataItem: false });
    }

    const riskAssessmentsSorted = orderBy(
      riskAssessmentsReducedByName(state),
      [
        (item) => item.unchangedTemplate,
        (item) => (item.probability ?? 0) * (item.consequence ?? 0),
        (item) => item.name,
      ],
      ['asc', 'desc', 'asc'],
    );
    categories.forEach((category) => {
      const riskAssessmentsInCategory = riskAssessmentsSorted.filter((r) => {
        if (category.substance) {
          return r.relatedSubstance !== undefined && r.relatedSubstance !== null && r.relatedSubstance !== '';
        }
        if (category.dataItem) {
          return r.relatedDataItem !== undefined && r.relatedDataItem !== null && r.relatedDataItem !== '';
        }
        if (category.pageId === pageIds.hse) {
          return r.pages?.includes(pageIds.hse) && !r.relatedSubstance;
        }
        if (category.pageId === pageIds.informationSecurity) {
          return r.pages?.includes(pageIds.informationSecurity) && !r.relatedDataItem;
        }
        return r.pages?.includes(category.pageId);
      });
      groups.push({
        themeName: category.name,
        themePageId: category.pageId,
        items: riskAssessmentsInCategory.map((r) => {
          return toListItem(r, hrefPrefix);
        }),
        themePageTarget: category.dataItem ? 'dataItem' : category.substance ? 'substance' : 'page',
      });
    });
    const themePageIds = themePages.map((p) => p.pageId);
    const ungrouped = riskAssessmentsSorted.filter((r) => {
      return (!r.pages || !r.pages.some((r) => themePageIds.includes(r))) && !r.relatedSubstance;
    });
    if (ungrouped.length) {
      groups.push({
        themeName: 'Annet',
        themePageId: 0,
        themePageTarget: 'page',
        items: ungrouped.map((r) => toListItem(r, hrefPrefix)),
      });
    }
  }
  // Drop groups that have neither risk assessments nor a title. These are empty
  // "ghost" cards (e.g. an unnamed theme page) that render as just a drop-shadow.
  // Empty but titled groups are kept so users can still navigate or add to them.
  const visibleGroups = groups.filter((g) => g.items.length > 0 || (g.themeName ?? '').trim() !== '');
  return sortBy(visibleGroups, (g) => g.items.length === 0);
}
